import { useEffect, useMemo, useState } from "react";
import type { CSSProperties, ReactNode } from "react";

import EmptyState from "../components/states/EmptyState";
import ErrorState from "../components/states/ErrorState";
import Skeleton from "../components/states/Skeleton";
import {
  GAP_HEADER,
  GAP_PCT_HEADER,
  GAP_SIGN_NOTE,
  INDEPENDENCE_CLASSES,
  INDEPENDENCE_LABELS,
  INDEPENDENCE_NOTES,
  NO_MATCH_COPY,
  PUBLISH_LOG_CAVEAT,
  REGISTER_ERROR_CAPTION,
  REGISTER_ERROR_TITLE,
  REGISTER_MISSING_CAPTION,
  REGISTER_MISSING_TITLE,
  SAME_SYSTEM_TITLE,
  SELF_CHECK_GAP_HEADER,
  VERDICTS,
  countVerdicts,
  defaultFilters,
  emptyRegisterCopy,
  filterRegister,
  flaggedVerdictCaveat,
  fmtBand,
  fmtGap,
  fmtGapPct,
  fmtRegisterValue,
  fmtTimestamp,
  groupRegister,
  inBandCaveat,
  independenceLabel,
  ourValueCell,
  summarizeRegister,
  targetKey,
  targetLabel,
  truncatedRegisterCopy,
  verdictLabel,
  verdictTone,
} from "../lib/boardVariance.mjs";
import type {
  BoardVarianceRow,
  RegisterFilters,
  RegisterMonthGroup,
  RegisterSummary,
  VerdictCounts,
} from "../lib/boardVariance.d.mts";
import {
  BoardVarianceFetchError,
  fetchBoardVarianceRows,
  fetchPublishTimes,
  type PublishTimes,
} from "../lib/boardVarianceData";
import { useExecKpiAvailability } from "../lib/execKpiData";
import { fmtValue, monthLabel } from "../lib/execKpi.mjs";
import {
  REASON_COPY,
  SOURCE_LABELS,
  indexLineSources,
  lineKey,
  lineSourceCell,
  summarizeLineSources,
} from "../lib/execLineSource.mjs";
import type { LineSourceEntry, LineSourceSummary } from "../lib/execLineSource.d.mts";
import { fetchExecLineSourceRows } from "../lib/execLineSourceData";
import {
  LINE_DEFINITION_DIFFERS,
  LINE_LIVE,
  derivedLineKey,
  derivedPageCell,
  indexDerivedLines,
  summarizeDerivedLines,
} from "../lib/execDerived.mjs";
import type { DerivedLineEntry, DerivedLineSummary } from "../lib/execDerived.d.mts";
import { fetchExecDerivedRegisterRows } from "../lib/execDerivedData";
import { useTenantId } from "../lib/tenant";

/**
 * MER-706: the staff variance register.
 *
 * For each line of the client's executive workbook and each month, the
 * client's figure beside the sum of our own inputs for the same sheet and
 * month, with the gap, the band verdict where a band exists, and whether the
 * comparison is independent, a same-system extract or unconfirmed. Read from
 * public.board_variance (0058), which the pipeline rebuilds after every
 * full-warehouse recompute. This page computes nothing: every value, gap and
 * verdict is the pipeline's, and lib/boardVariance.mjs only filters, groups
 * and formats.
 *
 * STAFF ONLY. Mounted in CustomerDashboard behind the staff session (a client
 * on the URL gets the staff-only notice) and linked only from the Internal
 * block; the table's sole read policy is public.is_staff(). It never touches
 * the client-visible Executive page: it shares only the exec_kpi availability
 * probe the shell already makes, to word the empty state.
 *
 * States: loading, "Register not available yet" when the table is not in the
 * database, a fixed-copy error card (the raw database message goes to
 * console.warn, never onto the page), an empty card worded by whether the
 * tenant has a workbook at all, and the register.
 *
 * AJ 2026-09-30: a tenant whose Executive page is the derived board
 * (`executive_page: derived`, Delta) never sees a workbook figure on that
 * page. For such a tenant the register opens with the page's line count by
 * state (live, awaiting by source, definition differs) and its Page shows
 * column reads the derived board (public.exec_derived): the page's figure
 * beside the Client column, which is where the workbook figure now lives.
 */

type LoadState =
  | { kind: "loading" }
  | { kind: "missing" }
  | { kind: "error" }
  | { kind: "ready"; rows: BoardVarianceRow[]; truncated: boolean };

const PANEL: CSSProperties = {
  background: "var(--panel)",
  border: "1px solid var(--border)",
  borderRadius: 12,
  boxShadow: "var(--shadow-card)",
};

const EYEBROW: CSSProperties = {
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: 0.6,
  color: "var(--text-2)",
  fontWeight: 600,
};

const MONO: CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontVariantNumeric: "tabular-nums",
};

const MUTED_SMALL: CSSProperties = {
  fontSize: 11.5,
  color: "var(--text-2)",
  lineHeight: 1.4,
};

const CONTROL: CSSProperties = {
  font: "inherit",
  fontSize: 12.5,
  padding: "5px 8px",
  border: "1px solid var(--border)",
  borderRadius: 6,
  background: "var(--panel)",
  color: "var(--text)",
  maxWidth: "100%",
};

const TH: CSSProperties = {
  ...EYEBROW,
  textAlign: "left",
  padding: "8px 10px",
  borderBottom: "1px solid var(--border)",
  whiteSpace: "nowrap",
  verticalAlign: "bottom",
};

const TD: CSSProperties = {
  padding: "8px 10px",
  borderBottom: "1px solid var(--border)",
  verticalAlign: "top",
  fontSize: 13,
  color: "var(--text)",
};

const NUM_TD: CSSProperties = { ...TD, ...MONO, textAlign: "right", whiteSpace: "nowrap" };

const NA = <span style={{ color: "var(--text-muted)" }}>n/a</span>;

const TONE_STYLE: Record<string, CSSProperties> = {
  good: {
    background: "var(--flag-green-wash)",
    color: "var(--flag-green)",
    border: "1px solid var(--flag-green)",
  },
  bad: {
    background: "var(--flag-red-wash)",
    color: "var(--flag-red)",
    border: "1px solid var(--flag-red)",
  },
  caution: {
    background: "var(--flag-yellow-wash)",
    color: "var(--flag-yellow)",
    border: "1px solid var(--flag-yellow)",
  },
  neutral: {
    background: "var(--bg)",
    color: "var(--text-2)",
    border: "1px solid var(--border)",
  },
};

function Chip(props: { tone: string; children: ReactNode; title?: string }) {
  return (
    <span
      title={props.title}
      style={{
        ...(TONE_STYLE[props.tone] ?? TONE_STYLE.neutral),
        display: "inline-block",
        fontSize: 11,
        fontWeight: 600,
        padding: "2px 8px",
        borderRadius: 999,
        whiteSpace: "nowrap",
      }}
    >
      {props.children}
    </span>
  );
}

function Badge(props: { label: string; children: ReactNode }) {
  return (
    <div
      style={{
        border: "1px solid var(--border)",
        borderRadius: 8,
        padding: "6px 10px",
        background: "var(--panel)",
        display: "grid",
        gap: 2,
        minWidth: 0,
      }}
    >
      <span style={EYEBROW}>{props.label}</span>
      <span style={{ ...MONO, fontSize: 12.5, color: "var(--text)", overflowWrap: "anywhere" }}>
        {props.children}
      </span>
    </div>
  );
}

function independenceTone(row: BoardVarianceRow): string {
  if (row.check_kind === "rollup_self_check") return "neutral";
  if (row.independence_class === "independent") return "good";
  if (row.independence_class === "same_system") return "caution";
  return "neutral";
}

export default function VarianceRegister(props: { derivedExecutive?: boolean }) {
  const derivedExecutive = props.derivedExecutive === true;
  const tenantId = useTenantId();
  const execAvailability = useExecKpiAvailability();
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [publish, setPublish] = useState<PublishTimes | null>(null);
  // S1-exec-from-imports: which source the Executive page shows per line.
  // null while loading or when the switch table is not there yet; the
  // register itself never waits on it.
  const [sources, setSources] = useState<Map<string, LineSourceEntry> | null>(null);
  // AJ 2026-09-30: the derived Executive page, line by line (derived tenants
  // only). null while loading, when not derived, or when the table or its
  // 0093 columns are not there yet.
  const [derived, setDerived] = useState<Map<string, DerivedLineEntry> | null>(null);

  useEffect(() => {
    if (!tenantId || !derivedExecutive) return;
    let cancelled = false;
    setDerived(null);
    fetchExecDerivedRegisterRows(tenantId)
      .then((result) => {
        if (!cancelled) setDerived(result.tableMissing ? null : indexDerivedLines(result.rows));
      })
      .catch((err: unknown) => {
        console.warn(
          `exec_derived read failed: ${err instanceof Error ? err.message : String(err)}`,
        );
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, derivedExecutive]);

  useEffect(() => {
    if (!tenantId || derivedExecutive) return;
    let cancelled = false;
    setSources(null);
    fetchExecLineSourceRows(tenantId)
      .then((result) => {
        if (!cancelled) setSources(result.tableMissing ? null : indexLineSources(result.rows));
      })
      .catch((err: unknown) => {
        console.warn(
          `exec_line_source read failed: ${err instanceof Error ? err.message : String(err)}`,
        );
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, derivedExecutive]);

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    setState({ kind: "loading" });
    fetchBoardVarianceRows(tenantId)
      .then((result) => {
        if (cancelled) return;
        setState({ kind: "ready", rows: result.rows, truncated: result.truncated });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.warn(
          `board_variance read failed: ${err instanceof Error ? err.message : String(err)}`,
        );
        if (err instanceof BoardVarianceFetchError && err.tableMissing) {
          setState({ kind: "missing" });
          return;
        }
        setState({ kind: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    setPublish(null);
    fetchPublishTimes(tenantId).then((times) => {
      if (!cancelled) setPublish(times);
    });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  let body: ReactNode;
  if (state.kind === "loading") {
    body = <Skeleton label="Loading the variance register" height={160} />;
  } else if (state.kind === "missing") {
    body = <EmptyState title={REGISTER_MISSING_TITLE} caption={REGISTER_MISSING_CAPTION} />;
  } else if (state.kind === "error") {
    body = <ErrorState title={REGISTER_ERROR_TITLE} message={REGISTER_ERROR_CAPTION} />;
  } else if (state.rows.length === 0) {
    const copy = emptyRegisterCopy(execAvailability);
    body = <EmptyState title={copy.title} caption={copy.caption} height={160} />;
  } else {
    body = (
      <RegisterBody
        rows={state.rows}
        truncated={state.truncated}
        publish={publish}
        sources={sources}
        derived={derivedExecutive ? derived : undefined}
      />
    );
  }

  return (
    <main style={{ padding: "24px 32px", display: "grid", gap: 16, minWidth: 0 }}>
      <header>
        <div style={EYEBROW}>Internal</div>
        <h1 style={{ margin: "4px 0 0 0", fontSize: 22 }}>Variance Register</h1>
        <p style={{ marginTop: 8, fontSize: 13, color: "var(--text-2)", maxWidth: 820 }}>
          Each line of this customer&apos;s executive workbook beside the sum of our own inputs for
          the same sheet and month, with the gap and the band verdict where a band is agreed. The
          pipeline computes every figure here; this page only shows them. Staff only: clients never
          see this page or its table.{" "}
          {derivedExecutive
            ? "The Executive page is the derived board: it shows our figure where we compute the line and names the missing source everywhere else, never the workbook's figure. The Page shows column says what the page shows for each line and month; the workbook's figure is the Client column."
            : "The Page shows column is the Executive page's source switch: our figure where we compute the line from the imports, otherwise the client's workbook, with the reason ours is missing."}
        </p>
      </header>
      {body}
    </main>
  );
}

function RegisterBody(props: {
  rows: BoardVarianceRow[];
  truncated: boolean;
  publish: PublishTimes | null;
  sources: Map<string, LineSourceEntry> | null;
  /** Derived tenants only: the derived page by line; null while loading or absent. */
  derived?: Map<string, DerivedLineEntry> | null;
}) {
  const { rows, truncated, publish, sources, derived } = props;
  const sourceSummary = useMemo(() => (sources ? summarizeLineSources(sources) : null), [sources]);
  const derivedSummary = useMemo(
    () => (derived ? summarizeDerivedLines(derived) : null),
    [derived],
  );
  const summary = useMemo(() => summarizeRegister(rows), [rows]);
  const [chosen, setChosen] = useState<{
    rows: BoardVarianceRow[];
    filters: RegisterFilters;
  } | null>(null);
  const defaults = useMemo(() => defaultFilters(summary), [summary]);
  // A new register (another tenant, a reload) starts from the defaults again.
  const filters = chosen && chosen.rows === rows ? chosen.filters : defaults;
  const update = (patch: Partial<RegisterFilters>) =>
    setChosen({ rows, filters: { ...filters, ...patch } });

  const filtered = useMemo(() => filterRegister(rows, filters), [rows, filters]);
  const groups = useMemo(() => groupRegister(filtered), [filtered]);
  const counts = useMemo(() => countVerdicts(filtered.variance), [filtered]);
  const nothing = filtered.variance.length === 0 && filtered.selfCheck.length === 0;

  return (
    <>
      {derived !== undefined ? <DerivedPageNote summary={derivedSummary} /> : null}
      <MetaBar summary={summary} publish={publish} />
      {truncated ? (
        <div
          role="status"
          style={{
            ...PANEL,
            padding: "10px 14px",
            borderColor: "var(--flag-yellow)",
            color: "var(--flag-yellow)",
            fontSize: 13,
          }}
        >
          {truncatedRegisterCopy(rows.length)}
        </div>
      ) : null}
      <IndependenceNote summary={summary} />
      {derived !== undefined ? null : <PageSourceNote summary={sourceSummary} />}
      <Filters summary={summary} filters={filters} onChange={update} />
      <Counts counts={counts} summary={summary} filters={filters} />
      {nothing ? (
        <EmptyState title={NO_MATCH_COPY} height={100} />
      ) : (
        groups.map((group) => (
          <section
            key={group.sheet}
            aria-label={`Sheet ${group.sheet}`}
            style={{ ...PANEL, padding: "14px 16px", display: "grid", gap: 14, minWidth: 0 }}
          >
            <header style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
              <h2 style={{ fontSize: 16, margin: 0 }}>{group.sheet}</h2>
              <span style={MUTED_SMALL}>{group.isRollup ? "Roll-up sheet" : group.label}</span>
            </header>
            {group.months.map((month) => (
              <MonthBlock
                key={month.period}
                sheet={group.sheet}
                month={month}
                sources={sources}
                derived={derived}
              />
            ))}
          </section>
        ))
      )}
    </>
  );
}

function MetaBar(props: { summary: RegisterSummary; publish: PublishTimes | null }) {
  const { summary, publish } = props;
  const pending = publish === null;
  const when = (iso: string | null | undefined) =>
    pending ? "loading" : (fmtTimestamp(iso) ?? "unknown");
  return (
    <section aria-label="Register provenance" style={{ display: "grid", gap: 8 }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
          gap: 8,
        }}
      >
        <Badge label="Workbook runs through">
          {summary.workbookLatestPeriod ? monthLabel(summary.workbookLatestPeriod) : "unknown"}
        </Badge>
        <Badge label="Workbook file">
          {summary.workbookFiles.length ? summary.workbookFiles.join(", ") : "unknown"}
        </Badge>
        <Badge label="Workbook last published">{when(publish?.workbookLastPublished)}</Badge>
        <Badge label="First workbook publish on record">
          {when(publish?.workbookFirstPublished)}
        </Badge>
        <Badge label="Register built">{fmtTimestamp(summary.computedAt) ?? "unknown"}</Badge>
        <Badge label="Register last published">{when(publish?.registerLastPublished)}</Badge>
      </div>
      <p style={MUTED_SMALL}>{PUBLISH_LOG_CAVEAT}</p>
      {summary.workbookPeriodCount > 1 || summary.workbookFiles.length > 1 ? (
        <p style={{ ...MUTED_SMALL, color: "var(--flag-yellow)" }}>
          The register rows name more than one workbook. The newest month is shown above; rebuild
          the register before reading it as one workbook.
        </p>
      ) : null}
    </section>
  );
}

function IndependenceNote(props: { summary: RegisterSummary }) {
  const { summary } = props;
  const independent = summary.lines.independent;
  return (
    <section
      aria-label={SAME_SYSTEM_TITLE}
      style={{
        ...PANEL,
        padding: "12px 16px",
        borderColor: "var(--flag-yellow)",
        background: "var(--flag-yellow-wash)",
        display: "grid",
        gap: 8,
      }}
    >
      <h2 style={{ fontSize: 14, margin: 0, color: "var(--text)" }}>{SAME_SYSTEM_TITLE}</h2>
      <p style={{ fontSize: 13, color: "var(--text)", lineHeight: 1.45 }}>
        {independent === 0
          ? "No line in this register is classed independent, so no in-band verdict on this page proves the client's figure. It shows only that our numbers agree with theirs."
          : `${independent} of ${summary.lines.total} workbook lines are classed independent. Only those can count as a check against the client's own books.`}
      </p>
      <dl style={{ display: "grid", gap: 6, margin: 0 }}>
        {INDEPENDENCE_CLASSES.map((cls) => (
          <div key={cls} style={{ display: "grid", gap: 2 }}>
            <dt style={{ fontSize: 12, fontWeight: 700, color: "var(--text)" }}>
              {INDEPENDENCE_LABELS[cls]}
            </dt>
            <dd style={{ margin: 0, fontSize: 12.5, color: "var(--text-2)", lineHeight: 1.4 }}>
              {INDEPENDENCE_NOTES[cls]}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function Filters(props: {
  summary: RegisterSummary;
  filters: RegisterFilters;
  onChange: (patch: Partial<RegisterFilters>) => void;
}) {
  const { summary, filters, onChange } = props;
  const label: CSSProperties = { display: "grid", gap: 4, fontSize: 12, color: "var(--text-2)" };
  return (
    <section
      aria-label="Register filters"
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 12,
        alignItems: "end",
      }}
    >
      <label style={label}>
        Sheet
        <select
          value={filters.sheet}
          onChange={(e) => onChange({ sheet: e.target.value })}
          style={CONTROL}
        >
          <option value="">All sheets</option>
          {summary.sheets.map((s) => (
            <option key={s.sheet} value={s.sheet}>
              {s.isRollup ? `${s.sheet} (roll-up)` : s.sheet}
            </option>
          ))}
        </select>
      </label>
      <label style={label}>
        Metric
        <select
          value={filters.target}
          onChange={(e) => onChange({ target: e.target.value })}
          style={CONTROL}
        >
          <option value="">All metrics and inputs</option>
          {summary.targets.map((key) => (
            <option key={key} value={key}>
              {targetLabel(key)}
            </option>
          ))}
        </select>
      </label>
      <label style={label}>
        Month
        <select
          value={filters.period}
          onChange={(e) => onChange({ period: e.target.value })}
          style={CONTROL}
        >
          <option value="">All months</option>
          {summary.periods.map((p) => (
            <option key={p} value={p}>
              {monthLabel(p)}
            </option>
          ))}
        </select>
      </label>
      <label
        style={{
          display: "flex",
          gap: 6,
          alignItems: "center",
          fontSize: 12.5,
          color: filters.target ? "var(--text-muted)" : "var(--text-2)",
          paddingBottom: 6,
        }}
      >
        <input
          type="checkbox"
          checked={filters.includeUnmapped}
          disabled={filters.target !== ""}
          onChange={(e) => onChange({ includeUnmapped: e.target.checked })}
        />
        Include unmapped lines ({summary.lines.unmapped})
      </label>
    </section>
  );
}

function Counts(props: {
  counts: VerdictCounts;
  summary: RegisterSummary;
  filters: RegisterFilters;
}) {
  const { counts, summary, filters } = props;
  const shown = VERDICTS.filter((v) => (counts.verdicts[v] ?? 0) > 0);
  const inBand = counts.verdicts.in_band ?? 0;
  return (
    <section aria-label="Verdict counts" style={{ display: "grid", gap: 6 }}>
      <div style={MUTED_SMALL}>
        {counts.total.toLocaleString("en-US")} line-months shown. Workbook lines:{" "}
        {summary.lines.mapped} mapped, {summary.lines.unmapped} unmapped ({summary.lines.notListed}{" "}
        not listed in the customer's line map at all)
        {filters.includeUnmapped && !filters.target ? "." : ", unmapped lines hidden."}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {shown.length === 0 ? (
          <span style={MUTED_SMALL}>No variance rows in this view.</span>
        ) : (
          shown.map((v) => (
            <Chip key={v} tone={verdictTone(v)}>
              {verdictLabel(v)}: {counts.verdicts[v]}
            </Chip>
          ))
        )}
      </div>
      {inBand > 0 ? (
        <div style={MUTED_SMALL}>
          Of the {inBand} in band: {counts.inBandBy.independent} independent,{" "}
          {counts.inBandBy.same_system} same-system extract, {counts.inBandBy.unconfirmed}{" "}
          unconfirmed.
        </div>
      ) : null}
    </section>
  );
}

function MonthBlock(props: {
  sheet: string;
  month: RegisterMonthGroup;
  sources: Map<string, LineSourceEntry> | null;
  derived?: Map<string, DerivedLineEntry> | null;
}) {
  const { sheet, month, sources, derived } = props;
  return (
    <div style={{ display: "grid", gap: 10, minWidth: 0 }}>
      <h3 style={{ fontSize: 13.5, margin: 0, color: "var(--text-2)" }}>{month.label}</h3>
      {month.variance.length > 0 ? (
        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 1080 }}>
            <caption style={{ ...MUTED_SMALL, textAlign: "left", paddingBottom: 6 }}>
              {sheet}, {month.label}: client figure beside ours. {GAP_SIGN_NOTE}
            </caption>
            <thead>
              <tr>
                <th scope="col" style={TH}>
                  Client line
                </th>
                <th scope="col" style={TH}>
                  Maps to
                </th>
                <th scope="col" style={{ ...TH, textAlign: "right" }}>
                  Client
                </th>
                <th scope="col" style={{ ...TH, textAlign: "right" }}>
                  Ours
                </th>
                <th scope="col" style={{ ...TH, textAlign: "right" }}>
                  {GAP_HEADER}
                </th>
                <th scope="col" style={{ ...TH, textAlign: "right" }}>
                  {GAP_PCT_HEADER}
                </th>
                <th scope="col" style={TH}>
                  Band
                </th>
                <th scope="col" style={TH}>
                  Verdict
                </th>
                <th scope="col" style={TH}>
                  Independence
                </th>
                <th scope="col" style={TH}>
                  Page shows
                </th>
              </tr>
            </thead>
            <tbody>
              {month.variance.map((row) => (
                <VarianceRow
                  key={`${row.client_label}|${row.period}`}
                  row={row}
                  source={
                    sources
                      ? (sources.get(lineKey(row.sheet, row.client_label)) ?? null)
                      : undefined
                  }
                  derived={
                    derived === undefined
                      ? undefined
                      : derived
                        ? (derived.get(derivedLineKey(row.sheet, row.client_label)) ?? null)
                        : undefined
                  }
                  derivedMode={derived !== undefined}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {month.selfCheck.length > 0 ? (
        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 760 }}>
            <caption style={{ ...MUTED_SMALL, textAlign: "left", paddingBottom: 6 }}>
              Workbook roll-up self-check, {month.label}: the client&apos;s roll-up against the sum
              of the client&apos;s own division sheets. Client against client, so it says nothing
              about our numbers.
            </caption>
            <thead>
              <tr>
                <th scope="col" style={TH}>
                  Roll-up line
                </th>
                <th scope="col" style={{ ...TH, textAlign: "right" }}>
                  Roll-up
                </th>
                <th scope="col" style={{ ...TH, textAlign: "right" }}>
                  Division sheets
                </th>
                <th scope="col" style={{ ...TH, textAlign: "right" }}>
                  {SELF_CHECK_GAP_HEADER}
                </th>
                <th scope="col" style={TH}>
                  Verdict
                </th>
              </tr>
            </thead>
            <tbody>
              {month.selfCheck.map((row) => (
                <tr key={`${row.client_label}|${row.period}`}>
                  <th scope="row" style={{ ...TD, textAlign: "left", fontWeight: 500 }}>
                    {row.client_label}
                  </th>
                  <td style={NUM_TD}>
                    {fmtRegisterValue(row.client_value, row.client_unit) ?? NA}
                  </td>
                  <td style={NUM_TD}>
                    {fmtRegisterValue(row.reference_value, row.client_unit) ?? NA}
                  </td>
                  <td style={NUM_TD}>{fmtGap(row.gap, row.client_unit) ?? NA}</td>
                  <td style={TD}>
                    <VerdictCell row={row} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

function VerdictCell(props: { row: BoardVarianceRow }) {
  const { row } = props;
  const caveat = inBandCaveat(row);
  const flagged = flaggedVerdictCaveat(row);
  const band = fmtBand(row);
  return (
    <div style={{ display: "grid", gap: 4, justifyItems: "start", maxWidth: 320 }}>
      <Chip tone={verdictTone(row.verdict)}>{verdictLabel(row.verdict)}</Chip>
      {caveat ? (
        <span style={{ ...MUTED_SMALL, color: "var(--flag-yellow)", fontWeight: 600 }}>
          {caveat}
        </span>
      ) : null}
      {flagged ? (
        <span style={{ ...MUTED_SMALL, color: "var(--flag-yellow)", fontWeight: 600 }}>
          {flagged}
        </span>
      ) : null}
      {row.verdict_reason ? <span style={MUTED_SMALL}>{row.verdict_reason}</span> : null}
      {row.check_kind === "rollup_self_check" && band ? (
        <span style={MUTED_SMALL}>Band: {band.toLowerCase()}</span>
      ) : null}
    </div>
  );
}

const OURS_NOTE: CSSProperties = {
  ...MUTED_SMALL,
  fontFamily: "var(--font-stack)",
  whiteSpace: "normal",
  textAlign: "right",
  maxWidth: 200,
  marginLeft: "auto",
};

function VarianceRow(props: {
  row: BoardVarianceRow;
  source?: LineSourceEntry | null;
  derived?: DerivedLineEntry | null;
  derivedMode?: boolean;
}) {
  const { row, source, derived, derivedMode = false } = props;
  const ours = ourValueCell(row);
  const gapUnit = row.our_unit ?? row.client_unit;
  const key = targetKey(row);
  return (
    <tr>
      <th scope="row" style={{ ...TD, textAlign: "left", fontWeight: 500, minWidth: 180 }}>
        <div>{row.client_label}</div>
        {row.basis_note ? (
          <div style={{ ...MUTED_SMALL, fontWeight: 400 }}>{row.basis_note}</div>
        ) : null}
      </th>
      <td style={{ ...TD, minWidth: 150 }}>
        {key ? (
          <>
            <div style={{ ...MONO, fontSize: 12 }}>{targetLabel(key)}</div>
            {row.metric_aggregation === "ratio" && row.compared_inputs ? (
              <div style={MUTED_SMALL}>Inputs: {row.compared_inputs.split(",").join(", ")}</div>
            ) : null}
          </>
        ) : (
          <span style={{ color: "var(--text-muted)" }}>Unmapped</span>
        )}
      </td>
      <td style={NUM_TD}>{fmtRegisterValue(row.client_value, row.client_unit) ?? NA}</td>
      <td style={NUM_TD}>
        <div>{ours.text ?? NA}</div>
        {ours.note ? <div style={OURS_NOTE}>{ours.note}</div> : null}
        {ours.flag ? (
          <div style={{ ...OURS_NOTE, color: "var(--flag-yellow)", fontWeight: 600 }}>
            {ours.flag}
          </div>
        ) : null}
      </td>
      <td style={NUM_TD}>{fmtGap(row.gap, gapUnit) ?? NA}</td>
      <td style={NUM_TD}>{fmtGapPct(row.gap_pct) ?? NA}</td>
      <td style={{ ...TD, whiteSpace: "nowrap" }}>{fmtBand(row) ?? NA}</td>
      <td style={TD}>
        <VerdictCell row={row} />
      </td>
      <td style={TD}>
        <div style={{ display: "grid", gap: 4, justifyItems: "start" }}>
          <Chip tone={independenceTone(row)}>{independenceLabel(row)}</Chip>
          {row.client_source ? (
            <span style={MUTED_SMALL}>Client source: {row.client_source.replace(/_/g, " ")}</span>
          ) : null}
        </div>
      </td>
      <td style={{ ...TD, minWidth: 190 }}>
        {derivedMode ? (
          <DerivedShowsCell row={row} entry={derived} />
        ) : (
          <PageShowsCell row={row} source={source} />
        )}
      </td>
    </tr>
  );
}

/**
 * S1-exec-from-imports: what the Executive page shows for this line and month.
 * `source` undefined means the switch table is not readable (not applied
 * yet); null means the line has no source row.
 */
function PageShowsCell(props: { row: BoardVarianceRow; source?: LineSourceEntry | null }) {
  const { row, source } = props;
  if (source === undefined) {
    return <span style={MUTED_SMALL}>{REASON_COPY.not_built}</span>;
  }
  const cell = lineSourceCell(source, row.period);
  const ours = cell.source === "ours";
  const unit = row.our_unit ?? row.client_unit;
  return (
    <div style={{ display: "grid", gap: 4, justifyItems: "start" }}>
      <Chip tone={ours ? "good" : "neutral"}>{SOURCE_LABELS[cell.source]}</Chip>
      {ours && cell.value !== null ? (
        <span style={{ ...MONO, fontSize: 12 }}>{fmtValue(cell.value, unit, null) ?? NA}</span>
      ) : null}
      {cell.reason ? <span style={MUTED_SMALL}>{cell.reason}</span> : null}
    </div>
  );
}

/**
 * S1-exec-from-imports: how many workbook lines the Executive page shows from
 * the imports, and why the rest keep the workbook, with the missing feeds
 * named. Lines, not cells.
 */
function PageSourceNote(props: { summary: LineSourceSummary | null }) {
  const { summary } = props;
  if (!summary) return null;
  const reasons = Object.entries(summary.reasons).sort((a, b) => b[1] - a[1]);
  const feeds = Object.entries(summary.feeds).sort((a, b) => b[1] - a[1]);
  return (
    <section aria-label="Executive page sources" style={{ ...PANEL, padding: "12px 14px" }}>
      <div style={{ fontSize: 13, fontWeight: 600 }}>
        Executive page: {summary.ours} of {summary.lines} lines show our figure from the imports
      </div>
      <ul style={{ margin: "6px 0 0 0", paddingLeft: 18, fontSize: 12.5 }}>
        {reasons.map(([code, n]) => (
          <li key={code}>
            {n} {n === 1 ? "line keeps" : "lines keep"} the workbook: {REASON_COPY[code] ?? code}
          </li>
        ))}
      </ul>
      {feeds.length ? (
        <div style={{ ...MUTED_SMALL, marginTop: 6 }}>
          Missing feeds:{" "}
          {feeds.map(([feed, n]) => `${feed} (${n} ${n === 1 ? "line" : "lines"})`).join("; ")}
        </div>
      ) : null}
    </section>
  );
}

/**
 * AJ 2026-09-30: what the derived Executive page shows for this line and
 * month. `entry` undefined means the derived board is not readable yet (the
 * table or its 0093 columns are missing); null means the line is not on it.
 * The workbook's figure is the Client column of this row, and the Gap column
 * (the pipeline's) is the difference; nothing is computed here.
 */
function DerivedShowsCell(props: { row: BoardVarianceRow; entry?: DerivedLineEntry | null }) {
  const { row, entry } = props;
  if (entry === undefined) {
    return <span style={MUTED_SMALL}>Derived board not built yet</span>;
  }
  const cell = derivedPageCell(entry, row.period);
  const tone =
    cell.state === LINE_LIVE
      ? "good"
      : cell.state === LINE_DEFINITION_DIFFERS
        ? "caution"
        : "neutral";
  const unit = entry?.unit ?? row.our_unit ?? row.client_unit;
  const gap = typeof row.gap === "number" ? row.gap : row.gap === null ? null : Number(row.gap);
  const differs = cell.value !== null && gap !== null && Number.isFinite(gap) && gap !== 0;
  return (
    <div style={{ display: "grid", gap: 4, justifyItems: "start" }}>
      <Chip tone={tone}>{cell.label}</Chip>
      {cell.value !== null ? (
        <span style={{ ...MONO, fontSize: 12 }}>
          {fmtValue(cell.value, unit, entry?.decimals ?? null) ?? NA}
        </span>
      ) : null}
      {differs ? (
        <span style={{ ...MUTED_SMALL, color: "var(--flag-yellow)", fontWeight: 600 }}>
          Differs from the workbook figure (Client); the difference is in Gap
        </span>
      ) : null}
      {cell.value !== null && row.metric_aggregation === "ratio" ? (
        <span style={MUTED_SMALL}>Ratio: the workbook figure is in Client</span>
      ) : null}
      {cell.reason ? <span style={MUTED_SMALL}>{cell.reason}</span> : null}
    </div>
  );
}

/**
 * AJ 2026-09-30: the derived Executive page's lines by state, so staff can
 * quote "N of M lines live", with the awaiting lines by the source they wait
 * for. Lines, not cells. Staff only, like the rest of this page.
 */
function DerivedPageNote(props: { summary: DerivedLineSummary | null }) {
  const { summary } = props;
  if (!summary) {
    return (
      <section aria-label="Executive page lines" style={{ ...PANEL, padding: "12px 14px" }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>
          Executive page (derived board): not built yet
        </div>
        <div style={MUTED_SMALL}>
          The derived board fills in after the next refresh once migration 0093 is applied.
        </div>
      </section>
    );
  }
  const sources = Object.entries(summary.bySource).sort(
    (a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : 1),
  );
  const lines = (n: number) => `${n.toLocaleString("en-US")} ${n === 1 ? "line" : "lines"}`;
  return (
    <section
      aria-label="Executive page lines"
      style={{ ...PANEL, padding: "12px 14px", display: "grid", gap: 8 }}
    >
      <div style={{ fontSize: 14, fontWeight: 700 }}>
        Executive page: {summary.live.toLocaleString("en-US")} of{" "}
        {summary.lines.toLocaleString("en-US")} lines live
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        <Chip tone="good">Live: {summary.live}</Chip>
        <Chip tone="neutral">Awaiting a source: {summary.awaiting}</Chip>
        <Chip tone="caution">Definition differs: {summary.definitionDiffers}</Chip>
      </div>
      {sources.length ? (
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, lineHeight: 1.5 }}>
          {sources.map(([source, n]) => (
            <li key={source}>
              {lines(n)} {source === "not yet mapped" ? "not yet mapped" : `awaiting ${source}`}
            </li>
          ))}
        </ul>
      ) : null}
      <div style={MUTED_SMALL}>
        The client page never shows a workbook figure. Where our figure differs from the
        workbook&apos;s, the workbook figure is in the Client column below and the difference in
        Gap. A line whose definition differs shows in the awaiting style on the page; its reason is
        in Page shows.
      </div>
    </section>
  );
}
